// Closing tab Step 4 (Create Loan).

import { useEffect, useState } from "react";
import { CheckCircle2, AlertCircle, ExternalLink } from "lucide-react";
import { Badge, Button, Card } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";
import { useCreateLoan, useCreateLoanPreview } from "@/hooks/dealFlow/useDealFlow";
import type { LoanCreationResultDto } from "@/api/dealFlow/types";
import { useDateFormatter } from "@/utils/datetime";
import { formatRateFractionAsPercent } from "@/lib/rateFormat";
import { formatCurrency } from "@/design-system/lib/format";

interface CreateLoanStepProps {
  dealId: string;
  /** When false, the button is disabled with the supplied lock reason. */
  unlocked: boolean;
  lockReason: string | null;
  /** When true (deal Closed), the step renders read-only: the loan has already
   *  been created, so the Create Loan action is disabled. */
  readOnly?: boolean;
}

export function CreateLoanStep({
  dealId,
  unlocked,
  lockReason,
  readOnly = false,
}: CreateLoanStepProps) {
  const create = useCreateLoan(dealId);
  // No preview fetch once the deal is Closed — the loan already exists.
  const preview = useCreateLoanPreview(dealId, unlocked && !readOnly);
  const toast = useToast();
  const { dateTime } = useDateFormatter();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [success, setSuccess] = useState<LoanCreationResultDto | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // The first-payment amount B2 will confirm (or adjust) — prefilled from the
  // suggested COMBINED period-0 debit (monthly P&I + the payment
  // administration fee) once the preview loads.
  const [amount, setAmount] = useState<string>("");
  const [amountError, setAmountError] = useState<string | null>(null);
  const previewRefusal =
    preview.error instanceof ApiError && preview.error.status === 422
      ? preview.error.errors.join("; ") || preview.error.message
      : null;

  useEffect(() => {
    if (preview.data && amount === "") {
      setAmount(preview.data.suggestedFirstPaymentAmount.toFixed(2));
    }
  }, [preview.data, amount]);

  const onConfirm = async () => {
    setErrorMessage(null);
    setAmountError(null);

    // The amount is editable; validate before firing. An empty value lets the
    // server fall back to the same suggested combined debit.
    const trimmed = amount.trim();
    let firstPaymentAmount: number | undefined;
    if (trimmed !== "") {
      const parsed = Number(trimmed);
      if (!Number.isFinite(parsed) || parsed <= 0) {
        setAmountError("Enter a valid first-payment amount greater than zero.");
        return;
      }
      firstPaymentAmount = parsed;
    }

    try {
      const result = await create.mutateAsync(firstPaymentAmount);
      setSuccess(result);
      setConfirmOpen(false);
      toast.success(`Deal closed and Loan ${result.loanNumber} created.`);
      // Surface a drift heads-up when boarding fell back to the default
      // placeholder TPS despite a recorded servicer decision.
      if (result.tpsDriftedToDefault) {
        toast.info(
          result.tpsDriftMessage ??
            "The selected servicer could not be honored — the loan boarded onto the default placeholder servicer. Reassign it in Loan Servicing.",
          { durationMs: 8000 },
        );
      }
    } catch (err) {
      const msg =
        err instanceof ApiError && err.errors.length
          ? err.errors.join("; ")
          : err instanceof Error
            ? err.message
            : "Create Loan failed.";
      setErrorMessage(msg);
      setConfirmOpen(false);
    }
  };

  if (success) {
    return (
      <Card>
        <div
          className="flex items-start gap-space-3"
          data-testid="closing-step-4-success"
        >
          <CheckCircle2
            size={28}
            className="text-status-success shrink-0"
            aria-hidden
          />
          <div className="flex-1">
            <h3 className="font-display text-h4 text-onyx m-0">
              Loan {success.loanNumber} created successfully
            </h3>
            <p className="text-small text-onyx m-0 mt-space-2">
              This deal is now Closed. Continue your work in Loan Servicing.
            </p>
            <p className="text-caption text-gray-mid m-0 mt-space-1">
              Created on {dateTime(success.createdAt)}.
            </p>
            <div className="mt-space-3">
              <a
                href={`/portfolio-admin/loans/${success.loanId}`}
                className="inline-flex items-center gap-space-1 text-gold-shadow underline"
              >
                Continue your work in Loan Servicing
                <ExternalLink size={12} aria-hidden />
              </a>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-space-3" data-testid="closing-step-4">
      <Card>
        <header className="flex items-start justify-between gap-space-3 mb-space-3">
          <div>
            <h3 className="font-display text-h4 text-onyx m-0">
              Step 4 · Create Loan in System
            </h3>
            <p className="text-caption text-gray-mid m-0">
              Final action — converts the deal into a Loan record in Loan
              Servicing and marks this deal Closed.
            </p>
          </div>
          {readOnly ? (
            <Badge status="finalized">Loan created</Badge>
          ) : unlocked ? (
            <Badge status="review">Ready</Badge>
          ) : (
            <Badge status="archived">Locked</Badge>
          )}
        </header>

        <ul className="text-small text-onyx m-0 mb-space-3 pl-space-4 list-disc">
          <li>Create a Loan record in Loan Servicing.</li>
          <li>Mark this deal as Closed.</li>
          <li>Hide this deal from the default Pipeline view.</li>
          <li>Hand off to Loan Servicing for UCC / insurance / boarding.</li>
        </ul>

        {/* The preview refuses (422) what Create Loan would refuse — today a
            missing Loan Amount in Underwriting Section 8 — so say so up front
            and hold the button until it is fixed. */}
        {previewRefusal && (
          <p
            className="text-small text-status-error m-0 mb-space-3"
            role="alert"
            data-testid="closing-step-4-precondition"
          >
            {previewRefusal}
          </p>
        )}

        {errorMessage && (
          <ErrorBanner
            message={errorMessage}
            onRetry={() => setConfirmOpen(true)}
            retryDisabled={previewRefusal !== null}
            onDismiss={() => setErrorMessage(null)}
          />
        )}

        <div className="flex items-center gap-space-2">
          <Button
            variant="primary"
            size="lg"
            onClick={() => setConfirmOpen(true)}
            disabled={readOnly || !unlocked || create.isPending || previewRefusal !== null}
            title={
              readOnly
                ? "This deal is closed — the loan has already been created."
                : !unlocked
                  ? (lockReason ?? "Step 4 is locked.")
                  : (previewRefusal ?? undefined)
            }
          >
            {create.isPending
              ? "Creating loan in Loan Servicing…"
              : errorMessage
                ? "Retry Create Loan"
                : "Create Loan"}
          </Button>
          {readOnly ? (
            <p className="text-caption text-gray-mid m-0">
              The loan has already been created. See the summary above.
            </p>
          ) : (
            !unlocked &&
            lockReason && (
              <p className="text-caption text-gray-mid m-0">{lockReason}</p>
            )
          )}
        </div>
      </Card>

      <Modal
        open={confirmOpen}
        onClose={() => !create.isPending && setConfirmOpen(false)}
        title="Create Loan and close this deal?"
        description="This will create a loan record in Loan Servicing AND close this deal. The deal will become read-only after closing."
        size="md"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setConfirmOpen(false)}
              disabled={create.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => void onConfirm()}
              disabled={create.isPending}
            >
              {create.isPending ? "Creating…" : "Yes, create the loan"}
            </Button>
          </>
        }
      >
        <p className="text-small text-onyx m-0">
          Heavy closing work (UCC filings, insurance, payment-servicer boarding,
          welcome packages) continues in Loan Servicing. After confirmation, all
          tabs on this Deal become read-only.
        </p>

        {preview.data && (
          <p className="text-small text-onyx m-0 mt-space-3" data-testid="create-loan-loan-amount">
            Loan amount:{" "}
            <strong>
              {formatCurrency(preview.data.loanAmount, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </strong>{" "}
            (Final Financial Addendum, Underwriting Section 8)
          </p>
        )}

        <div className="mt-space-4">
          <label
            htmlFor="first-payment-amount"
            className="block text-small font-display text-onyx mb-space-1"
          >
            First payment (recorded at closing, 100% to Phoenix)
          </label>
          <input
            id="first-payment-amount"
            type="text"
            inputMode="decimal"
            className="w-full rounded-md border border-border-light px-space-3 py-space-2 text-small"
            value={amount}
            onChange={(e) => {
              setAmount(e.target.value);
              setAmountError(null);
            }}
            disabled={create.isPending}
            data-testid="first-payment-amount"
          />
          {preview.data && (
            <p className="text-caption text-gray-mid m-0 mt-space-1">
              Suggested ${preview.data.suggestedFirstPaymentAmount.toFixed(2)}
              {/* Break the total down when the server sends the components, so
                  staff can see the fee they are collecting rather than wonder
                  why the suggestion no longer matches the amortization figure.
                  Falls back to the plain total on an older payload. */}
              {/* Single-line literals: a multi-line template literal keeps its
                  source indentation in the VALUE (embedded newlines and long runs
                  of spaces mid-sentence). HTML whitespace collapsing hides that
                  when rendered, but the string is then not what it appears to be
                  anywhere it is read directly — a title attribute, an exact-text
                  assertion. */}
              {preview.data.scheduledPandI != null &&
              preview.data.paymentAdministrationFee != null
                ? ` — monthly P&I $${preview.data.scheduledPandI.toFixed(2)} plus the $${preview.data.paymentAdministrationFee.toFixed(2)} payment administration fee${preview.data.paymentAdministrationFeeRate ? ` (${formatRateFractionAsPercent(preview.data.paymentAdministrationFeeRate)})` : ""}, billed together as one debit`
                : " (computed monthly P&I)"}
              {" — adjust if the closing statement differs."}
            </p>
          )}
          {amountError && (
            <p
              className="text-caption text-status-error m-0 mt-space-1"
              role="alert"
            >
              {amountError}
            </p>
          )}
        </div>
      </Modal>
    </div>
  );
}

function ErrorBanner({
  message,
  onRetry,
  retryDisabled,
  onDismiss,
}: {
  message: string;
  onRetry: () => void;
  retryDisabled: boolean;
  onDismiss: () => void;
}) {
  return (
    <div
      className="border border-status-error/40 bg-status-error/5 rounded-md px-space-3 py-space-3 mb-space-3 flex items-start gap-space-2"
      data-testid="closing-step-4-error"
      role="alert"
    >
      <AlertCircle size={18} className="text-status-error shrink-0" aria-hidden />
      <div className="flex-1 min-w-0">
        <div className="font-display text-small text-status-error m-0">
          Loan creation failed
        </div>
        <p className="text-caption text-onyx m-0 mt-space-1 break-words">
          {message}
        </p>
        <p className="text-caption text-gray-mid m-0 mt-space-1">
          The deal remains in the Closing stage. Retry below, or
          {" "}
          <a
            href="mailto:engineering@phoenix.capital?subject=Loan%20creation%20failure"
            className="underline"
          >
            report the issue
          </a>
          .
        </p>
      </div>
      <Button variant="secondary" size="sm" onClick={onRetry} disabled={retryDisabled}>
        Retry
      </Button>
      <Button variant="secondary" size="sm" onClick={onDismiss}>
        Dismiss
      </Button>
    </div>
  );
}
